/**
 * Purchase Order & Warehouse Execution Data Model
 * Manages POs, staging workflows, and load-out checklists.
 */

import type { MaterialLineItem } from "./types";

/* ══════════════════════════════════════════════════════════
   Purchase Orders
   ══════════════════════════════════════════════════════════ */

export type POStatus = "Draft" | "Ordered" | "Partially Received" | "Received";

export interface POLineItem {
  sku: string;
  itemName: string;
  quantityOrdered: number;
  quantityReceived: number;
  unitCost: number;
  received: boolean;
}

export interface PurchaseOrder {
  id: string;
  jobId: string;
  /** Multiple jobs linked to this PO (batch ordering) */
  jobIds: string[];
  status: POStatus;
  supplier: string;
  supplierId?: string;
  items: POLineItem[];
  totalCost: number;
  createdAt: string;
  orderedAt?: string;
  receivedAt?: string;
  notes: string;
}

export function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

export function resolveSupplierName(po: { supplier: string; supplierId?: string }, vendors: { vendorId: string; vendorName: string }[] = []): string {
  const name = po.supplier ?? "";
  if (name && !isUuidLike(name)) return name;
  if (po.supplierId && vendors.length > 0) {
    const vendor = vendors.find(v => v.vendorId === po.supplierId);
    if (vendor?.vendorName) return vendor.vendorName;
  }
  return name || "Unknown Vendor";
}

/* ══════════════════════════════════════════════════════════
   Staging
   ══════════════════════════════════════════════════════════ */

export type StagingStatus = "Not Staged" | "Partially Staged" | "Fully Staged";

export interface StagingRecord {
  jobId: string;
  status: StagingStatus;
  items: { sku: string; itemName: string; staged: boolean }[];
  stagedBy?: string;
  stagedAt?: string;
}

/* ══════════════════════════════════════════════════════════
   Load-Out Checklist
   ══════════════════════════════════════════════════════════ */

export interface LoadOutChecklist {
  jobId: string;
  allMaterialsLoaded: boolean;
  correctItemsVerified: boolean;
  toolsReady: boolean;
  specialInstructionsReviewed: boolean;
  signedOffBy?: string;
  signedOffAt?: string;
  complete: boolean;
}

/* ══════════════════════════════════════════════════════════
   Auto PO Generation
   ══════════════════════════════════════════════════════════ */

const SUPPLIERS = ["MainLine Supply Co.", "ProBath Distributors", "National Fixture Warehouse"];

export function generateDraftPO(
  jobId: string,
  materialsList: MaterialLineItem[],
  materialReadiness?: string,
): PurchaseOrder | null {
  if (["Fully Received", "Staged for Job", "Loaded for Crew"].includes(String(materialReadiness ?? "").trim())) {
    return null;
  }
  const missingItems = materialsList.filter((m) => m.missingItems > 0);
  if (missingItems.length === 0) return null;

  const items: POLineItem[] = missingItems.map((m) => ({
    sku: m.sku,
    itemName: m.itemName,
    quantityOrdered: m.missingItems,
    quantityReceived: 0,
    unitCost: 0, // Cost would come from supplier pricing
    received: false,
  }));

  const totalCost = items.reduce((sum, i) => sum + i.quantityOrdered * i.unitCost, 0);

  return {
    id: `PO-${jobId}-${Date.now().toString(36).toUpperCase()}`,
    jobId,
    jobIds: [jobId],
    status: "Draft",
    supplier: SUPPLIERS[0],
    items,
    totalCost,
    createdAt: new Date().toISOString(),
    notes: `Auto-generated for ${jobId} — ${missingItems.length} missing items`,
  };
}

/** Create a staging record from a materials list */
export function createStagingRecord(jobId: string, materialsList: MaterialLineItem[]): StagingRecord {
  return {
    jobId,
    status: "Not Staged",
    items: materialsList.map((m) => ({
      sku: m.sku,
      itemName: m.itemName,
      staged: false,
    })),
  };
}

/** Compute staging status from items */
export function computeStagingStatus(items: StagingRecord["items"]): StagingStatus {
  if (items.length === 0) return "Not Staged";
  const stagedCount = items.filter((i) => i.staged).length;
  if (stagedCount === 0) return "Not Staged";
  if (stagedCount === items.length) return "Fully Staged";
  return "Partially Staged";
}

/** Create a blank load-out checklist */
export function createLoadOutChecklist(jobId: string): LoadOutChecklist {
  return {
    jobId,
    allMaterialsLoaded: false,
    correctItemsVerified: false,
    toolsReady: false,
    specialInstructionsReviewed: false,
    complete: false,
  };
}

/** Check if load-out is complete */
export function isLoadOutComplete(checklist: LoadOutChecklist): boolean {
  return (
    checklist.allMaterialsLoaded &&
    checklist.correctItemsVerified &&
    checklist.toolsReady &&
    checklist.specialInstructionsReviewed
  );
}
